import { useState } from "react";
import { Download, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConfigStore } from "../../../../store/configStore";
import {
  processTemplate,
  processStaticLayerTemplate,
} from "../../../utils/templateLoader";
import { isStaticSpineTemplateType } from "../../../../types/spine-template";
import { getFetcher } from "api/swr";
import { toast } from "@/components/ui/use-toast";

interface DownloadRelatedTemplateButtonProps {
  templateId: string;
}

export function DownloadRelatedTemplateButton({
  templateId,
}: DownloadRelatedTemplateButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    setDialogOpen(true);
    setLoadingMessage("正在获取模板信息...");

    try {
      const template = await getFetcher(`/spine-templates/${templateId}`);

      const { addLoadedRelatedTemplate } = useConfigStore.getState();

      if (!template.spineZipUrl) {
        if (isStaticSpineTemplateType(template.type)) {
          const processed = await processStaticLayerTemplate(
            templateId,
            template,
            (msg) => setLoadingMessage(msg),
          );
          addLoadedRelatedTemplate({
            id: templateId,
            type: template.type,
            config: processed.templateConfig,
            spineData: null,
            parsedSkins: [],
            spineVersion: template.spineVersion,
          });
        } else {
          throw new Error("该关联模版没有 Spine ZIP，且不是静态图层模版");
        }
      } else {
        const processed = await processTemplate(templateId, template, (msg) =>
          setLoadingMessage(msg),
        );
        addLoadedRelatedTemplate({
          id: templateId,
          type: template.type,
          config: processed.templateConfig,
          spineData: processed.spineData,
          parsedSkins: processed.parsedSkins,
          spineVersion: template.spineVersion,
        });
      }

      setLoadingMessage("下载完成！");
      // 短暂展示完成状态后关闭弹窗
      setTimeout(() => {
        setDialogOpen(false);
        setIsDownloading(false);
        setLoadingMessage("");
      }, 600);
    } catch (error) {
      console.error(`下载关联模版 ${templateId} 失败:`, error);
      toast({
        variant: "destructive",
        title: "下载失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
      setDialogOpen(false);
      setIsDownloading(false);
      setLoadingMessage("");
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        disabled={isDownloading}
        title="下载关联模版数据"
      >
        <Download className="h-3.5 w-3.5" />
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-sm"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>下载关联模版</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {loadingMessage === "下载完成！" ? (
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            ) : (
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            )}
            <div className="text-center">
              <p className="text-sm font-medium mb-1">{templateId}</p>
              <p className="text-sm text-muted-foreground">
                {loadingMessage || "准备中..."}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
